import type { QuoteStreamResponse } from "@/shared/types/quoteStream";

const baseURL = import.meta.env.VITE_APP_ENDPOINT_URL;

function getAuthToken(): string | null {
  try {
    const persisted = localStorage.getItem("persist:protego-auth");
    if (persisted) {
      const root = JSON.parse(persisted);
      const user = root.user ? JSON.parse(root.user) : null;
      const token = user?.access_token || root?.access_token;
      if (token) return token;
    }
  } catch {
    // fall through
  }

  return localStorage.getItem("otp_access_token");
}

type StreamQuotesParams<TRequestBody> = {
  endpointPath: string;
  requestBody: TRequestBody;
  onQuoteReceived: (quote: QuoteStreamResponse) => void;
  onStreamError?: (error: unknown) => void;
  onStreamComplete?: () => void;
};

export async function streamQuotes<TRequestBody>({
  endpointPath,
  requestBody,
  onQuoteReceived,
  onStreamError,
  onStreamComplete,
}: StreamQuotesParams<TRequestBody>): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${baseURL}${endpointPath}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(requestBody),
  });

  if (!res.ok || !res.body) {
    const errorText = await res.text().catch(() => "");
    throw new Error(`SSE connection failed with ${res.status}${errorText ? `: ${errorText}` : ""}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split("\n\n");
    buffer = events.pop() || "";

    for (const event of events) {
      if (!event.startsWith("data:")) continue;

      try {
        const data = JSON.parse(event.replace(/^data:\s*/, ""));

        if (data.error) {
          onStreamError?.(data.error);
          continue;
        }

        if (data.insurer && data.plan) {
          onQuoteReceived(data);
          continue;
        }

        if (data.status === "completed") {
          onStreamComplete?.();
          return;
        }
      } catch (error) {
        console.error("SSE parse error", error);
      }
    }
  }
}
